import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CreditCard, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const STATUS_BADGE = {
  active: "bill.activeBadge",
  trialing: "bill.trialBadge",
  past_due: "bill.pastDueBadge",
  unpaid: "bill.pastDueBadge",
  canceled: "bill.canceledBadge",
  incomplete: "bill.pastDueBadge",
  incomplete_expired: "bill.canceledBadge",
};

export default function Subscription() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const { data: config } = useQuery({
    queryKey: ["billing-config"],
    queryFn: async () => (await api.get("/billing/config")).data,
  });
  const { data: status, refetch } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  // Return from Stripe Checkout: confirm entitlement SERVER-SIDE (never trust the URL alone).
  useEffect(() => {
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info(t("bill.canceledMsg"));
      setParams({}, { replace: true });
      return;
    }
    if (sid) {
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.post("/billing/refresh", {});
          qc.setQueryData(["billing-status"], data);
          if (data.active) {
            toast.success(t("bill.successMsg"));
            setParams({}, { replace: true });
            return;
          }
        } catch (_) { /* keep polling */ }
        if (tries < 6) setTimeout(poll, 2000);
        else setParams({}, { replace: true });
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceLabel = config
    ? `${config.amount.toLocaleString(undefined, { style: "currency", currency: (config.currency || "eur").toUpperCase() })}`
    : "";

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e, t)); setBusy(false); }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(apiError(e, t)); setBusy(false); }
  };

  const active = status?.active;
  const badgeKey = status?.status ? STATUS_BADGE[status.status] : null;

  return (
    <div className="min-h-screen bg-[#F8FAF8] pb-24">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-[#E2EAE5] bg-[#F8FAF8]/90 px-4 py-3 backdrop-blur">
        <button data-testid="subscription-back" onClick={() => navigate("/dashboard")} className="p-1 text-[#2D5A4C]">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display text-lg font-bold text-[#1A2E26]">{t("bill.title")}</h1>
      </header>

      <main data-testid="subscription-page" className="mx-auto max-w-md px-4 py-6">
        <div className="rounded-3xl border border-[#E2EAE5] bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-[#2D5A4C]">
            <Sparkles size={18} />
            <span className="font-display text-base font-bold">{config?.plan_name || "ESVAN Premium"}</span>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span data-testid="subscription-price" className="font-display text-4xl font-bold text-[#1A2E26]">{priceLabel}</span>
            {config?.interval && <span className="text-sm text-[#7A9488]">/{t("bill.perMonth")}</span>}
          </div>
          {config?.trial_days > 0 && !active && (
            <p className="mt-1 text-xs text-[#7A9488]">{t("bill.trialLine").replace("{n}", config.trial_days)}</p>
          )}

          {active ? (
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-2 rounded-xl bg-[#EDF7F2] px-3 py-2 text-sm font-semibold text-[#1B6943]">
                <Check size={16} />
                <span data-testid="subscription-status-badge">{badgeKey ? t(badgeKey) : status.status}</span>
              </div>
              {status.cancel_at_period_end && status.current_period_end && (
                <p className="text-xs text-[#B36B00]">{t("bill.cancelsAt")} {new Date(status.current_period_end).toLocaleDateString()}</p>
              )}
              {!status.cancel_at_period_end && status.current_period_end && (
                <p className="text-xs text-[#7A9488]">{t("bill.renews")} {new Date(status.current_period_end).toLocaleDateString()}</p>
              )}
              <button data-testid="subscription-manage" onClick={manage} disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2D5A4C] py-3 text-sm font-semibold text-white active:scale-[0.99] disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} {t("bill.manage")}
              </button>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {badgeKey && status?.status !== "none" && (
                <div className="rounded-xl bg-[#FDECEA] px-3 py-2 text-sm font-semibold text-[#C03A2B]" data-testid="subscription-status-badge">{t(badgeKey)}</div>
              )}
              <button data-testid="subscription-subscribe" onClick={subscribe} disabled={busy || !config}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2D5A4C] py-3 text-sm font-semibold text-white active:scale-[0.99] disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {t("bill.subscribe")}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
